import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Warehouse, GitBranch, Plus, Pencil, Trash2, Loader2, Package, ShoppingCart, Users, MapPin, Phone, ArrowRightLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Branch {
  id: number;
  storeId: number;
  name: string;
  type: "warehouse" | "branch";
  address: string | null;
  phone: string | null;
  isActive: boolean;
  productCount: number;
  saleCount: number;
  staffCount: number;
  createdAt: string;
}

const EMPTY_FORM = { name: "", type: "branch" as "branch" | "warehouse", address: "", phone: "" };

export default function Branches() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  useEffect(() => {
    if (location.includes("add=1")) setIsOpen(true);
  }, [location]);

  const { data: branches = [], isLoading, error, failureReason } = useQuery<Branch[]>({
    queryKey: ["branches"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/branches`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err: any = new Error(body.error ?? "Failed to load branches");
        err.upgrade = body.upgrade ?? false;
        err.status = res.status;
        throw err;
      }
      return res.json();
    },
  });

  const needsUpgrade = (failureReason as any)?.upgrade === true || (failureReason as any)?.status === 403;

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  }

  function openEdit(b: Branch) {
    setEditing(b);
    setForm({ name: b.name, type: b.type, address: b.address ?? "", phone: b.phone ?? "" });
    setIsOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const url = editing ? `${BASE}/api/branches/${editing.id}` : `${BASE}/api/branches`;
      const method = editing ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to save");
      toast({ title: editing ? "Branch updated" : "Branch created" });
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      setIsOpen(false);
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    setDeleting(id);
    try {
      const res = await fetch(`${BASE}/api/branches/${id}`, { method: "DELETE", credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to delete");
      toast({ title: "Branch deleted" });
      queryClient.invalidateQueries({ queryKey: ["branches"] });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  }

  const warehouse = branches.find(b => b.type === "warehouse");
  const storeBranches = branches.filter(b => b.type === "branch");

  return (
    <>
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <GitBranch className="w-6 h-6 text-primary" /> Branch Management
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Manage your store locations and central warehouse.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.location.href = `${BASE}/stock-transfers?add=1`}>
              <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer Stock
            </Button>
            <Button onClick={openAdd}>
              <Plus className="w-4 h-4 mr-1" /> Add Branch
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && needsUpgrade && (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <GitBranch className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold mb-1">Multi-Branch is a Pro Feature</h2>
              <p className="text-muted-foreground text-sm max-w-sm">
                Upgrade to Pro or Enterprise to manage multiple store locations, warehouses, and stock transfers between branches.
              </p>
            </div>
            <a href={`${BASE}/subscribe`} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors">
              Upgrade to Pro
            </a>
          </div>
        )}

        {!isLoading && error && !needsUpgrade && (
          <div className="flex items-center justify-center py-20 text-destructive text-sm">
            {(error as Error).message}
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Warehouse */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Central Warehouse
              </h2>
              {warehouse ? (
                <BranchCard branch={warehouse} onEdit={openEdit} onDelete={handleDelete} deleting={deleting} />
              ) : (
                <button
                  onClick={() => { setForm({ ...EMPTY_FORM, type: "warehouse" }); setEditing(null); setIsOpen(true); }}
                  className="w-full border-2 border-dashed border-muted-foreground/30 rounded-xl p-8 text-center hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <Warehouse className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="font-medium text-sm">No warehouse yet</p>
                  <p className="text-muted-foreground text-xs mt-1">Click to set up your central warehouse</p>
                </button>
              )}
            </div>

            {/* Branches */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Store Branches ({storeBranches.length})
              </h2>
              {storeBranches.length === 0 ? (
                <button
                  onClick={openAdd}
                  className="w-full border-2 border-dashed border-muted-foreground/30 rounded-xl p-8 text-center hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <GitBranch className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="font-medium text-sm">No branches yet</p>
                  <p className="text-muted-foreground text-xs mt-1">Add your first store location</p>
                </button>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {storeBranches.map(b => (
                    <BranchCard key={b.id} branch={b} onEdit={openEdit} onDelete={handleDelete} deleting={deleting} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Branch" : "Add Branch"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editing && (
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v as any }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="branch">Branch / Store Location</SelectItem>
                    <SelectItem value="warehouse" disabled={!!warehouse}>
                      Warehouse (central stock)
                      {warehouse ? " — already exists" : ""}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input placeholder={form.type === "warehouse" ? "e.g. Main Warehouse" : "e.g. Lekki Branch"}
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input placeholder="e.g. 12 Allen Avenue, Ikeja" value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input placeholder="e.g. 08012345678" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BranchCard({ branch, onEdit, onDelete, deleting }: {
  branch: Branch;
  onEdit: (b: Branch) => void;
  onDelete: (id: number) => void;
  deleting: number | null;
}) {
  const isWarehouse = branch.type === "warehouse";
  return (
    <Card className={`relative ${isWarehouse ? "border-primary/40 bg-primary/5" : ""}`}>
      {isWarehouse && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-primary rounded-t-xl" />
      )}
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            {isWarehouse ? <Warehouse className="w-4 h-4 text-primary" /> : <GitBranch className="w-4 h-4 text-muted-foreground" />}
            {branch.name}
          </span>
          <Badge variant={branch.isActive ? "default" : "secondary"} className="text-xs">
            {branch.isActive ? (isWarehouse ? "Warehouse" : "Active") : "Inactive"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {branch.address && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3 h-3 shrink-0" /> {branch.address}
          </p>
        )}
        {branch.phone && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Phone className="w-3 h-3 shrink-0" /> {branch.phone}
          </p>
        )}
        <div className="grid grid-cols-3 gap-2 pt-1">
          {[
            { icon: Package, value: branch.productCount, label: "Products" },
            { icon: ShoppingCart, value: branch.saleCount, label: "Sales" },
            { icon: Users, value: branch.staffCount, label: "Staff" },
          ].map(({ icon: Icon, value, label }) => (
            <div key={label} className="bg-muted/50 rounded-lg p-2 text-center">
              <Icon className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-1" />
              <p className="font-semibold text-sm">{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="default" className="flex-1"
            onClick={() => window.location.href = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/inventory?add=1&branchId=${branch.id}`}>
            <Package className="w-3.5 h-3.5 mr-1" /> Add Product
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(branch)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive"
            onClick={() => onDelete(branch.id)} disabled={deleting === branch.id}>
            {deleting === branch.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
